'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import {
  ProductImage as _ProductImage,
  ProductVideo as _ProductVideo,
} from '@peerless/data-access/etilize-api-client';
import videoThumb from '@peerless/ui-assets/images/photo-gallery-video-thumb.jpg';
import { getProductImage } from '../utils/getProductImage';

export type ProductImage = _ProductImage | _ProductVideo;

export interface ProductImageProps {
  image: ProductImage;
  lazy?: boolean;
  thumbnail?: boolean;
}

export function ProductImage({ image, lazy = false, thumbnail = false }: ProductImageProps) {
  const t = useTranslations('ProductGallery');

  const fallBackImageAlt = (imageType: ProductImage['type']) => {
    const altPrefix = 'alternate-image';

    if (imageType.startsWith(altPrefix)) {
      const fractions = imageType.split(/(\d+)/);
      return t(altPrefix, { sequence: fractions[1] });
    }

    return t(imageType as Exclude<ProductImage['type'], `alternate-image${number}`>);
  };

  const text = image.text ?? fallBackImageAlt(image.type);

  if (image.type === 'video') {
    if (thumbnail) {
      return (
        <Image
          src={videoThumb}
          alt={text}
          fill
          sizes={
            thumbnail
              ? `(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 15vw`
              : `(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw`
          }
          style={{ objectFit: 'contain' }}
        />
      );
    }

    return (
      <video
        controls
        preload={lazy ? 'none' : 'auto'}
        style={{
          margin: 'auto',
          maxWidth: '100%',
          alignSelf: 'center',
          objectFit: 'contain',
        }}
      >
        <source src={image.src} type="video/mp4" />
        {text}
      </video>
    );
  }

  if (thumbnail) {
    // get smallest image size for thumbnail
    const src = getProductImage([image], 75) ?? image.src;

    return (
      <Image
        src={src}
        alt={text}
        fill
        priority
        unoptimized
        loading="eager"
        sizes={`(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 15vw`}
        style={{ objectFit: 'contain' }}
      />
    );
  }

  return (
    <Image
      src={image.src}
      alt={text}
      fill
      unoptimized
      loading={lazy ? 'lazy' : 'eager'}
      sizes={`(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw`}
      style={{ objectFit: 'contain' }}
    />
  );
}
